import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

import avatarFallback from "@/assets/UI/avatar.png";
import coinIcon from "@/assets/UI/coin.webp";
import paymentSubmissionsIcon from "@/assets/UI/paymentSubmissions.webp";

import { formatDateTime } from "@/utils/formatDateTime";
import s from "./WalletDetail.module.scss";

const GROUP_OPTIONS = [
  { value: "none", label: "No grouping" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const getWeekRangeLabel = (dateValue) => {
  const date = new Date(dateValue);
  const day = date.getDay();
  const start = new Date(date);
  start.setDate(date.getDate() - day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
};

const buildGroupLabel = (groupBy, dateValue) => {
  const date = new Date(dateValue);
  switch (groupBy) {
    case "weekly":
      return getWeekRangeLabel(dateValue);
    case "monthly":
      return date.toLocaleString(undefined, { month: "long", year: "numeric" });
    case "yearly":
      return String(date.getFullYear());
    default:
      return "All transactions";
  }
};

const WalletDetail = () => {
  const { walletId } = useParams();
  const navigate = useNavigate();

  const [wallet, setWallet] = useState(null);
  const [user, setUser] = useState(null);
  const [avatar, setAvatar] = useState(null);

  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  const [error, setError] = useState("");
  const [transactionsError, setTransactionsError] = useState("");

  const [transactions, setTransactions] = useState([]);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    groupBy: "none",
  });

  useEffect(() => {
    let isMounted = true;

    const fetchWallet = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await api.get(`/v1/admin/finance/wallets/${walletId}`);
        if (!isMounted) return;
        setWallet(res.data?.wallet ?? null);
        setUser(res.data?.user ?? null);
        setAvatar(res.data?.avatar ?? null);
      } catch (err) {
        if (!isMounted) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load wallet detail",
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchWallet();

    return () => {
      isMounted = false;
    };
  }, [walletId]);

  const fetchTransactions = async () => {
    try {
      setTransactionsLoading(true);
      setTransactionsError("");
      const res = await api.get(
        `/v1/admin/finance/wallets/${walletId}/transactions`,
        {
          params: {
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined,
          },
        },
      );
      setTransactions(res.data?.data ?? []);
    } catch (err) {
      setTransactionsError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load wallet transactions",
      );
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId]);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    fetchTransactions();
  };

  const resetFilters = () => {
    setFilters({ startDate: "", endDate: "", groupBy: "none" });
  };

  const groupedTransactions = useMemo(() => {
    if (!transactions.length) return [];
    const groupBy = filters.groupBy;
    if (groupBy === "none") {
      return [{ label: "All transactions", items: transactions }];
    }
    return transactions.reduce((acc, tx) => {
      const label = buildGroupLabel(groupBy, tx.createdAt);
      const group = acc.find((entry) => entry.label === label);
      if (group) group.items.push(tx);
      else acc.push({ label, items: [tx] });
      return acc;
    }, []);
  }, [filters.groupBy, transactions]);

  const renderPrettyDateTime = (value) => {
    const dt = formatDateTime(value);
    if (!dt) return <span className={s.dash}>-</span>;

    const hasParts =
      dt.day !== undefined && dt.suffix && dt.month && dt.year !== undefined;

    return (
      <div className={s.dateTime}>
        <div className={s.date}>
          {hasParts ? (
            <>
              {dt.day}
              <sup className={s.sup}>{dt.suffix}</sup> {dt.month} {dt.year}
            </>
          ) : (
            dt.date
          )}
        </div>
        <div className={s.time}>{dt.time}</div>
      </div>
    );
  };

  const memberProfilePath = wallet?.userId
    ? `/admin/members/single/${wallet.userId}`
    : null;

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerLeft}>
          <p className={s.eyebrow}>Finance • Wallet detail</p>
          <h1 className={s.title}>Member wallet</h1>
          <p className={s.subtitle}>
            Review balances, filter wallet transactions, and inspect activity.
          </p>
        </div>

        <div className={s.actions}>
          <button className={s.btn} onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </header>

      {loading ? (
        <div className={s.stateBox}>Loading wallet…</div>
      ) : error ? (
        <div className={`${s.stateBox} ${s.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      ) : wallet ? (
        <>
          {/* Wallet header */}
          <section className={s.card}>
            <div className={s.walletHeader}>
              <div className={s.walletIdentity}>
                {memberProfilePath ? (
                  <Link to={memberProfilePath} className={s.memberLink}>
                    <span className={s.avatarRing}>
                      <img
                        src={avatar?.url || avatarFallback}
                        alt=""
                        className={s.avatarLg}
                      />
                    </span>

                    <span className={s.memberText}>
                      <span className={s.muted}>Member</span>
                      <span className={s.userName}>
                        {user?.username || "Unknown user"}
                      </span>
                    </span>
                  </Link>
                ) : (
                  <>
                    <span className={s.avatarRing}>
                      <img
                        src={avatar?.url || avatarFallback}
                        alt=""
                        className={s.avatarLg}
                      />
                    </span>

                    <div className={s.memberText}>
                      <p className={s.muted}>Member</p>
                      <p className={s.userName}>
                        {user?.username || "Unknown user"}
                      </p>
                    </div>
                  </>
                )}

                <span
                  className={`${s.statusPill} ${
                    s[
                      `status_${String(wallet.status || "active").toLowerCase()}`
                    ] || ""
                  }`}
                >
                  {wallet.status}
                </span>
              </div>

              <div className={s.walletMeta}>
                <div>
                  <p className={s.muted}>Current balance</p>
                  <div className={s.metric}>
                    <img className={s.coinMd} src={coinIcon} alt="" />
                    <span className={s.metricValue}>
                      {wallet.balanceNoty ?? 0}
                    </span>
                    <span className={s.metricUnit}>NOTY</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Filters */}
          <section className={s.card}>
            <h2 className={s.sectionTitle}>Transaction filters</h2>

            <form className={s.filters} onSubmit={applyFilters}>
              <label className={s.formRow}>
                <span className={s.label}>Start date</span>
                <input
                  className={s.input}
                  type="date"
                  value={filters.startDate}
                  onChange={handleFilterChange("startDate")}
                />
              </label>

              <label className={s.formRow}>
                <span className={s.label}>End date</span>
                <input
                  className={s.input}
                  type="date"
                  value={filters.endDate}
                  onChange={handleFilterChange("endDate")}
                />
              </label>

              <label className={s.formRow}>
                <span className={s.label}>Group by</span>
                <div className={s.selectWrap}>
                  <select
                    className={s.select}
                    value={filters.groupBy}
                    onChange={handleFilterChange("groupBy")}
                  >
                    {GROUP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <div className={s.inlineActions}>
                <button className={`${s.btn} ${s.btnPrimary}`} type="submit">
                  Apply
                </button>
                <button
                  className={`${s.btn} ${s.btnSecondary}`}
                  type="button"
                  onClick={resetFilters}
                >
                  Reset
                </button>
              </div>
            </form>
          </section>

          {transactionsError && (
            <div className={`${s.stateBox} ${s.stateBoxError}`}>
              <strong>Something went wrong</strong>
              <span>{transactionsError}</span>
            </div>
          )}

          {/* Transactions */}
          <section className={s.card}>
            <h2 className={s.sectionTitle}>Wallet transactions</h2>

            {transactionsLoading ? (
              <div className={s.stateBox}>Loading transactions…</div>
            ) : transactions.length === 0 ? (
              <div className={s.stateBox}>No transactions found.</div>
            ) : (
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Reason</th>
                      <th>Amount (Noty)</th>
                      <th>Date</th>
                      <th>Balance after</th>
                      <th className={s.thActions}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {groupedTransactions.map((group) => (
                      <Fragment key={group.label}>
                        <tr className={s.groupRow}>
                          <td colSpan={5}>
                            <div className={s.groupRowInner}>
                              <strong>{group.label}</strong>
                              <span className={s.groupCount}>
                                {group.items.length} transactions
                              </span>
                            </div>
                          </td>
                        </tr>

                        {group.items.map((tx) => {
                          const isCredit = tx.type === "credit";

                          return (
                            <tr key={tx.id} className={s.row}>
                              <td data-label="Reason">
                                <div className={s.reasonCell}>
                                  <div className={s.reasonTop}>
                                    <span className={s.reasonText}>
                                      {tx.reason || "-"}
                                    </span>
                                  </div>

                                  <div className={s.reasonMeta}>
                                    <span
                                      className={`${s.typeBadge} ${
                                        isCredit ? s.typeCredit : s.typeDebit
                                      }`}
                                    >
                                      {tx.type}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td data-label="Amount (Noty)">
                                <div
                                  className={`${s.amount} ${
                                    isCredit ? s.amountCredit : s.amountDebit
                                  }`}
                                >
                                  <span className={s.sign}>
                                    {isCredit ? "+" : "-"}
                                  </span>
                                  <span className={s.amountValue}>
                                    {tx.amountNoty}
                                  </span>
                                  <img
                                    className={s.coinSm}
                                    src={coinIcon}
                                    alt=""
                                  />
                                </div>
                              </td>

                              <td data-label="Date" className={s.dateCell}>
                                {renderPrettyDateTime(tx.createdAt)}
                              </td>

                              <td data-label="Balance after">
                                <div className={s.balanceAfter}>
                                  <span className={s.balanceAfterValue}>
                                    {tx.balanceAfter ?? "-"}
                                  </span>
                                  <img
                                    className={s.coinSm}
                                    src={coinIcon}
                                    alt=""
                                  />
                                </div>
                              </td>

                              <td
                                data-label="Actions"
                                className={s.actionsCell}
                              >
                                <Link
                                  className={s.iconBtn}
                                  to={`/admin/finance/wallets/${wallet.walletId}/transactions/${tx.id}`}
                                  title="View"
                                  aria-label="View"
                                >
                                  <img
                                    className={s.iconBtnImg}
                                    src={paymentSubmissionsIcon}
                                    alt=""
                                  />
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : (
        <div className={s.stateBox}>Wallet not found.</div>
      )}
    </div>
  );
};

export default WalletDetail;
